
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatPaymentMethod } from '@/utils/formatters';
import { PaymentMethodType } from '@/types';
import { useToast } from '@/hooks/use-toast';

interface PaymentEligibilityCardProps {
  balance: number;
  paymentMethod?: PaymentMethodType;
  threshold: number;
  canRequestPayment: boolean;
}

const PaymentEligibilityCard: React.FC<PaymentEligibilityCardProps> = ({
  balance,
  paymentMethod,
  threshold,
  canRequestPayment
}) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const methodLabel = paymentMethod ? formatPaymentMethod(paymentMethod) : 'any payment method';

  const handleRequestPayment = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('PaymentEligibilityCard: Request payment button clicked');
    console.log('PaymentEligibilityCard: Can request payment?', canRequestPayment);
    console.log('PaymentEligibilityCard: Balance:', balance);
    console.log('PaymentEligibilityCard: Threshold:', threshold);
    
    if (!canRequestPayment) {
      toast({
        title: 'Cannot Request Payment',
        description: `Your balance of $${balance.toFixed(2)} is below the minimum threshold of $${threshold.toFixed(2)}.`,
        variant: 'destructive',
      });
      return;
    }

    if (!paymentMethod) {
      toast({
        title: 'No Payment Method',
        description: 'Please set up a payment method before requesting payment.',
        variant: 'destructive',
      });
      navigate('/author/payment-preferences');
      return;
    }

    console.log('PaymentEligibilityCard: Navigating to payment request');
    // Use window.location.href for direct navigation
    window.location.href = '/author/payments/request';
  };

  return (
    <Card className="mb-6">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold">Payment Eligibility</h3>
            <p className="text-sm text-gray-500">
              {canRequestPayment 
                ? `You are eligible to request payment via ${methodLabel}`
                : `You need at least $${threshold.toFixed(2)} to request payment via ${methodLabel}`
              }
            </p>
            <p className="text-sm mt-1">
              Current balance: <span className="font-medium">${balance.toFixed(2)}</span>
            </p>
          </div>
          <Button 
            className={canRequestPayment ? "bg-royalty-600 hover:bg-royalty-700" : ""}
            variant={canRequestPayment ? "default" : "outline"}
            onClick={handleRequestPayment}
            type="button"
          >
            {canRequestPayment ? 'Request Payment' : `Below Threshold ($${threshold.toFixed(2)})`}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentEligibilityCard;
